import castArray from 'lodash/castArray';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import uniqueId from 'lodash/uniqueId';
import React, { Component } from 'react';

import {
  devWarning,
  HasControlledProps,
  TestIdProps,
  withControlledProps,
} from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import {
  FormControlProvider,
  FormError,
  FormErrorsNormalized,
  FormProvider,
  FormValues,
} from '@tanium/react-form-context';
import { FormButtons, FormButtonsProps, FormHeader } from '@tanium/react-form-ui';
import { ThemeDefinition } from '@tanium/react-theme-definition';

import { FormFooter } from './FormFooter';

export interface FormSubmitEvent<TFields> {
  value: FormValues<TFields>;
  event: React.FormEvent<HTMLFormElement>;
}

export interface FormSubmitEventHandler<TFields> {
  (e: FormSubmitEvent<TFields>): void;
}

export interface FormValueChangeEvent<TFields> {
  value: Partial<FormValues<TFields>>;
}

export interface FormValueChangeEventHandler<TFields> {
  (e: FormValueChangeEvent<TFields>): void;
}

export type FormErrors<TFields> = { [k in keyof TFields]?: FormError | FormError[] };

type StickyFooterProps =
  | {
      /**
       * Whether the form's footer should be sticky (i.e., use `position: sticky`). When true,
       * whenever the form extends beyond the bottom of the viewport, the footer (e.g., submit
       * buttons) will stick to the bottom of the viewport so as to remain visible and accessible to
       * the user. You must also provide a `stickyFooterBg` when this prop is true. Defaults to
       * false.
       */
      stickyFooter?: false;
      /**
       * The background to use for the sticky footer when it is "stuck". This prop should generally
       * be set to match the background of the Form's container. This prop is required when
       * `stickyFooter` is true, and has no effect when `stickyFooter` is `false`.
       */
      stickyFooterBg?: ThemeDefinition<string>;
    }
  | {
      /**
       * Whether the form's footer should be sticky (i.e., use `position: sticky`). When true,
       * whenever the form extends beyond the bottom of the viewport, the footer (e.g., submit
       * buttons) will stick to the bottom of the viewport so as to remain visible and accessible to
       * the user. You must also provide a `stickyFooterBg` when this prop is true. Defaults to
       * false.
       */
      stickyFooter: true;
      /**
       * The background to use for the sticky footer when it is "stuck". This prop should generally
       * be set to match the background of the Form's container. This prop is required when
       * `stickyFooter` is true, and has no effect when `stickyFooter` is `false`.
       */
      stickyFooterBg: ThemeDefinition<string>;
    };

export type Props<TFields = {}> = Pick<
  FormButtonsProps,
  'submitButtonLabel' | 'cancelButtonLabel' | 'submitButtonDisabled'
> &
  TestIdProps &
  StickyFooterProps & {
    /**
     * The title to display on the form.
     */
    title?: React.ReactNode;
    /**
     * Whether to show a key above the form indicating that a red star means
     * "required". Enabled by default; should always be enabled if the form
     * contains any required inputs.
     */
    showRequiredKey?: boolean;
    /**
     * Callback to be invoked when user clicks the cancel button. If omitted, no
     * cancel button will be shown by default.
     */
    onCancel?: () => void;
    /**
     * Callback to be invoked when the form is submitted. Callback will receive
     * the form's values as well as the original event.
     */
    onSubmit?: FormSubmitEventHandler<TFields>;
    /**
     * Button(s) to show at the bottom of the form. By default, a submit button
     * and cancel button (if the `onCancel` callback is specified) are shown. A
     * Fragment can be used to pass multiple elements, which will be automatically
     * laid out by the form component.
     *
     * Note that this prop is only meant for buttons; it is not a general purpose footer.
     */
    buttons?: React.ReactNode;
    /**
     * The values of the form's fields, for use as a controlled component. You
     * should specify either this or `initialValue`.
     *
     * Note that if you omit any form fields from this object, they may not be
     * reported in callbacks (like onSubmit) if the user hasn't interacted with
     * them.
     */
    value?: FormValues<TFields>;
    /**
     * The initial values of the form's fields, for use as an uncontrolled
     * component. You should specify either this or `value`.
     *
     * Note that if you omit any form fields from this object, they may not be
     * reported in callbacks (like onSubmit) if the user hasn't interacted with
     * them.
     */
    initialValue?: FormValues<TFields>;
    /**
     * Any error(s) corresponding to the form's fields.
     */
    errors?: FormErrors<TFields>;
    /**
     * Callback to be invoked when a value in the form changes, e.g., when a user
     * types in a text input. The callback will receive only the changed value(s).
     */
    onValueChange?: FormValueChangeEventHandler<TFields>;
    /**
     * The form's contents.
     */
    children: React.ReactNode;
  };

interface State<TFields = {}> {
  value: FormValues<TFields> | {};
  errors: FormErrors<TFields>;
}

class Form<TFields = {}> extends Component<Props<TFields> & HasControlledProps, State<TFields>> {
  public static defaultProps: Partial<Props> = {
    showRequiredKey: true,
    stickyFooter: false,
  };

  private readonly prefix = uniqueId('form-');

  public constructor(props: Props<TFields> & HasControlledProps) {
    super(props);

    this.state = {
      errors: {},
      value: props.initialValue || {},
    };
  }

  private getErrors = () =>
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.props.isPropControlled('errors') ? this.props.errors! : this.state.errors;

  private getNormalizedErrors = () => {
    const mappedErrors = mapValues(this.getErrors(), (e) => castArray(e).filter(Boolean));
    return pickBy(
      mappedErrors,
      (e: typeof mappedErrors[keyof typeof mappedErrors]) => !!e.length,
    ) as FormErrorsNormalized<TFields>;
  };

  private getValue = () =>
    this.props.isPropControlled('value')
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.props.value!
      : (this.state.value as FormValues<TFields>);

  private setValue = (value: Partial<FormValues<TFields>>) => {
    if (!this.props.isPropControlled('value')) {
      this.setState((state) => ({ value: { ...state.value, ...value } }));
    }

    if (this.props.onValueChange) {
      this.props.onValueChange({ value });
    }
  };

  public render() {
    const errors = this.getNormalizedErrors();

    devWarning(
      !this.props.stickyFooter || !!this.props.stickyFooterBg,
      'A Form was rendered with `stickyFooter` set to `true` without a `stickyFooterBg` specified. `stickyFooterBg` must be specified in order for `stickyFooter` to be enabled. `stickyFooter` will be ignored.',
    );

    const enableStickyFooter = !!(this.props.stickyFooter && this.props.stickyFooterBg);

    return (
      <FormProvider errors={errors} prefix={this.prefix} value={this.getValue()}>
        <FormControlProvider setValue={this.setValue}>
          <form
            data-test-id={this.props['data-test-id']}
            onSubmit={(e) => {
              e.preventDefault();

              if (this.props.onSubmit) {
                this.props.onSubmit({
                  value: this.getValue(),
                  event: e,
                });
              }
            }}
          >
            {(this.props.title || this.props.showRequiredKey) && (
              <Box mb={2}>
                <FormHeader showRequiredKey={this.props.showRequiredKey} title={this.props.title} />
              </Box>
            )}
            <div>
              {this.props.children}
              {(this.props.buttons || this.props.buttons === undefined) && (
                <FormFooter enableSticky={enableStickyFooter} stickyBg={this.props.stickyFooterBg}>
                  <FormButtons
                    cancelButtonLabel={this.props.cancelButtonLabel}
                    submitButtonLabel={this.props.submitButtonLabel}
                    onCancelButtonClick={
                      this.props.onCancel &&
                      (() => {
                        if (this.props.onCancel) {
                          this.props.onCancel();
                        }
                      })
                    }
                    submitButtonDisabled={this.props.submitButtonDisabled}
                  >
                    {this.props.buttons}
                  </FormButtons>
                </FormFooter>
              )}
            </div>
          </form>
        </FormControlProvider>
      </FormProvider>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default withControlledProps<Props<any> & HasControlledProps, HTMLElement>([
  'value',
  'errors',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
])(Form) as React.ComponentType<Props<any>>;
